const DATA_URI_PREFIX = 'data:image/png;base64,';

const num = value => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

const isCoordinate = c =>
    !!c && typeof c.latitude === 'number' && typeof c.longitude === 'number';

// V4 declares `longitude` first — read by name, never by position.
const toPoint = c => [c.latitude, c.longitude];

// Per the SDK doc V4 stores [TL, TR, BR] while leaflet needs BL, so derive the
// parallelogram's fourth point. If migrated plans render sheared, the backend stores BL.
function deriveBottomLeft(topLeft, topRight, bottomRight) {
    return [
        topLeft[0] + bottomRight[0] - topRight[0],
        topLeft[1] + bottomRight[1] - topRight[1],
    ];
}

function deriveBottomRight(topLeft, topRight, bottomLeft) {
    return [
        topRight[0] + bottomLeft[0] - topLeft[0],
        topRight[1] + bottomLeft[1] - topLeft[1],
    ];
}

export function v4CoordinatesToCorners(coordinates) {
    const points = (Array.isArray(coordinates) ? coordinates : [])
        .filter(isCoordinate)
        .map(toPoint);
    if (points.length < 3) return {topLeft: [], topRight: [], bottomLeft: []};
    const [topLeft, topRight, bottomRight] = points;
    return {topLeft, topRight, bottomLeft: deriveBottomLeft(topLeft, topRight, bottomRight)};
}

export function cornersToV4Coordinates(topLeft, topRight, bottomLeft, altitude) {
    const corners = [topLeft, topRight, bottomLeft];
    if (corners.some(c => !Array.isArray(c) || c.length < 2)) return [];
    const tl = corners[0].map(num);
    const tr = corners[1].map(num);
    const bl = corners[2].map(num);
    const br = deriveBottomRight(tl, tr, bl);
    return [tl, tr, br].map(([latitude, longitude]) => ({latitude, longitude, altitude}));
}

// V4 is opacity 0..1, V3 is the inverse: transparency 0..100.
export const v4OpacityToV3Transparency = opacity =>
    Math.round((1 - Math.min(1, Math.max(0, num(opacity)))) * 100);

export const v3TransparencyToV4Opacity = transparency =>
    Math.min(1, Math.max(0, (100 - num(transparency)) / 100));

// V4 blob is bare base64, V3 url is a data URI.
export const blobToDataUri = blob =>
    blob ? (/^data:/.test(blob) ? blob : `${DATA_URI_PREFIX}${blob}`) : '';

export const dataUriToBlob = url =>
    url ? String(url).replace(/^data:[^,]*,/, '') : '';

// V4 name is V3 description. accountId is the owner uuid: V4-native sessions
// key accounts by uuid.
export function mapV4SiteToV3(v4Site, accountName = '') {
    const {topLeft, topRight, bottomLeft} = v4CoordinatesToCorners(v4Site.coordinates);
    return {
        id: v4Site.id,
        uuid: v4Site.id,
        description: v4Site.name || '',
        topLeft,
        topRight,
        bottomLeft,
        url: blobToDataUri(v4Site.background?.blob),
        altitude: Math.round(num(v4Site.altitude)),
        accountId: v4Site.owner_id || 0,
        accountName,
        opacity: v4OpacityToV3Transparency(v4Site.background?.opacity),
        source: 'v4',
        v4OwnerId: v4Site.owner_id || null,
        v4Data: v4Site,
    };
}

// No blob, no background: Update is a full replace, so a body built from a list
// row (blob stripped) would erase the floor plan.
export function v3SiteToV4Body(site) {
    const get = key => (site?.get ? site.get(key) : site?.[key]);
    const altitude = Math.round(num(get('altitude')));
    const blob = dataUriToBlob(get('url'));
    const body = {
        name: get('description') || '',
        altitude,
        coordinates: cornersToV4Coordinates(
            get('topLeft'), get('topRight'), get('bottomLeft'), altitude,
        ),
    };
    if (blob) {
        body.background = {blob, opacity: v3TransparencyToV4Opacity(get('opacity'))};
    }
    return body;
}
